// Čtečka PDF: vykreslení stránek knihovnou pdf.js (Mozilla, Apache-2.0) – soubory jsou u nás v assets/pdfjs (viz VERZE.txt),
// nic se nenačítá z internetu, skripty v PDF se nespouštějí. Stránky pod sebou, přiblížení (tlačítka, kolečko s Ctrl,
// štípnutí), vykreslují se jen viditelné stránky, hledání v textu po stránkách. Stejné na počítači i na mobilu.
import { h } from '@core/dom';
import { t } from '@core/i18n';
import { loader } from '@core/components/misc';

interface Viewport { width: number; height: number }
interface RenderTask { promise: Promise<void>; cancel(): void }
interface PdfPage { getViewport(o: { scale: number }): Viewport; render(o: { canvasContext: CanvasRenderingContext2D; viewport: Viewport; canvas: HTMLCanvasElement }): RenderTask; getTextContent(): Promise<{ items: { str?: string }[] }> }
interface PdfDoc { numPages: number; getPage(n: number): Promise<PdfPage> }
interface LoadingTask { promise: Promise<PdfDoc>; destroy(): Promise<void>; onProgress?: (p: { loaded: number; total: number }) => void }
interface PdfLib { GlobalWorkerOptions: { workerSrc: string }; getDocument(o: Record<string, unknown>): LoadingTask }

let libPromise: Promise<PdfLib> | null = null;
/** Map.getOrInsert/getOrInsertComputed (ES2026) používá pdf.js 6; starší prohlížeče je nemají – doplnit stejně chovající se funkce. */
function polyfillUpsert(): void {
  for (const proto of [Map.prototype, WeakMap.prototype] as unknown as Record<string, unknown>[]) {
    if (typeof proto['getOrInsertComputed'] !== 'function') {
      Object.defineProperty(proto, 'getOrInsertComputed', { configurable: true, writable: true, value(this: Map<unknown, unknown>, k: unknown, f: (k: unknown) => unknown) { if (this.has(k)) return this.get(k); const v = f(k); this.set(k, v); return v; } });
    }
    if (typeof proto['getOrInsert'] !== 'function') {
      Object.defineProperty(proto, 'getOrInsert', { configurable: true, writable: true, value(this: Map<unknown, unknown>, k: unknown, v: unknown) { if (this.has(k)) return this.get(k); this.set(k, v); return v; } });
    }
  }
}
/** Knihovna se načte až při prvním otevření PDF, z našeho serveru. */
function loadLib(base: string): Promise<PdfLib> {
  polyfillUpsert();
  libPromise ??= (import(/* @vite-ignore */ base + 'pdf.min.mjs') as Promise<PdfLib>).then((m) => {
    m.GlobalWorkerOptions.workerSrc = base + 'pdf.worker.min.mjs';
    return m;
  });
  return libPromise;
}

export interface PdfView {
  el: HTMLElement;
  zoomIn(): void;
  zoomOut(): void;
  fit(): void;
  goTo(n: number): void;
  find(q: string): Promise<void>;
  findNext(dir: 1 | -1): void;
  hasHits(): boolean;
  destroy(): void;
}

interface PageSlot { wrap: HTMLElement; canvas: HTMLCanvasElement | null; page: PdfPage | null; w: number; h: number; renderedScale: number; task: RenderTask | null; text: string | null }

export function createPdfView(opts: { url: string; assetsBase: string; onStatus: (page: number, pages: number, percent: number, extra?: string) => void; onError: (message: string) => void }): PdfView {
  const pagesEl = h('div', { class: 'viewer-pdf-pages' });
  const el = h('div', { class: 'viewer-pdf' }, pagesEl);
  const slots: PageSlot[] = [];
  let doc: PdfDoc | null = null;
  let loading: LoadingTask | null = null; // uvolnění dokumentu a pracovního vlákna jde přes úlohu načtení
  let fitScale = 1; // měřítko „na šířku okna“ podle první stránky
  let zoom = 1; // násobek fitScale
  let current = 1;
  let destroyed = false;
  let hits: { page: number; count: number }[] = [];
  let hitIndex = -1;
  let extra: string | undefined;
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const scale = (): number => fitScale * zoom;

  const status = (): void => opts.onStatus(current, slots.length, Math.round(scale() * 100), extra);

  const observer = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const i = Number((e.target as HTMLElement).dataset['page']) - 1;
      const s = slots[i];
      if (!s) continue;
      if (e.isIntersecting) void render(s, i + 1);
      else if (s.renderedScale > 0 && Math.abs(i + 1 - current) > 6) release(s); // daleké stránky uvolnit (paměť na mobilu)
    }
  }, { root: el, rootMargin: '600px 0px' });

  function size(s: PageSlot): void {
    s.wrap.style.width = Math.round(s.w * scale()) + 'px';
    s.wrap.style.height = Math.round(s.h * scale()) + 'px';
  }

  function release(s: PageSlot): void {
    s.task?.cancel();
    s.task = null;
    s.canvas?.remove();
    s.canvas = null;
    s.renderedScale = 0;
  }

  async function render(s: PageSlot, n: number): Promise<void> {
    if (!doc || destroyed) return;
    if (!s.page) {
      s.page = await doc.getPage(n);
      const v = s.page.getViewport({ scale: 1 });
      if (v.width !== s.w || v.height !== s.h) { s.w = v.width; s.h = v.height; size(s); }
    }
    const sc = scale();
    if (s.renderedScale === sc || destroyed) return;
    s.task?.cancel();
    const viewport = s.page.getViewport({ scale: sc * dpr });
    const canvas = h('canvas') as HTMLCanvasElement;
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    canvas.style.width = Math.round(s.w * sc) + 'px';
    canvas.style.height = Math.round(s.h * sc) + 'px';
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const task = s.page.render({ canvasContext: ctx, viewport, canvas });
    s.task = task;
    try {
      await task.promise;
    } catch {
      return; // zrušeno (změna měřítka, zavření)
    }
    if (s.task !== task || destroyed) return;
    s.canvas?.remove();
    s.canvas = canvas;
    s.wrap.prepend(canvas);
    s.renderedScale = sc;
    s.task = null;
  }

  function rerenderVisible(): void {
    for (const s of slots) { s.renderedScale = 0; size(s); }
    const r = el.getBoundingClientRect();
    slots.forEach((s, i) => { const b = s.wrap.getBoundingClientRect(); if (b.bottom > r.top - 600 && b.top < r.bottom + 600) void render(s, i + 1); });
    status();
  }

  function setZoom(z: number, keepRatio = true): void {
    const nz = Math.max(0.25, Math.min(6, z));
    if (nz === zoom) return;
    const ratio = keepRatio && el.scrollHeight > 0 ? el.scrollTop / el.scrollHeight : 0;
    zoom = nz;
    rerenderVisible();
    el.scrollTop = ratio * el.scrollHeight;
  }

  function updateCurrent(): void {
    const top = el.getBoundingClientRect().top + 8;
    let best = 1;
    let bestDist = Infinity;
    slots.forEach((s, i) => { const d = Math.abs(s.wrap.getBoundingClientRect().top - top); if (d < bestDist) { bestDist = d; best = i + 1; } });
    if (best !== current) { current = best; status(); }
  }
  el.addEventListener('scroll', () => { window.requestAnimationFrame(updateCurrent); });

  // kolečko s Ctrl = přiblížení (bez Ctrl posun); štípnutí na dotyku
  el.addEventListener('wheel', (e) => { if (e.ctrlKey) { e.preventDefault(); setZoom(zoom * (e.deltaY < 0 ? 1.1 : 0.9)); } }, { passive: false });
  let pinch: { dist: number; zoom: number } | null = null;
  el.addEventListener('touchstart', (e) => { if (e.touches.length === 2) { const [a, b] = [e.touches[0], e.touches[1]]; if (a && b) pinch = { dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), zoom }; } }, { passive: true });
  el.addEventListener('touchmove', (e) => {
    if (!pinch || e.touches.length !== 2) return;
    e.preventDefault();
    const [a, b] = [e.touches[0], e.touches[1]];
    if (a && b) pagesEl.style.transform = `scale(${Math.max(0.25, Math.min(6, pinch.zoom * (Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) / pinch.dist))) / zoom})`;
  }, { passive: false });
  el.addEventListener('touchend', () => {
    if (!pinch) return;
    const m = /scale\(([\d.]+)\)/.exec(pagesEl.style.transform);
    pagesEl.style.transform = '';
    const z = m ? zoom * Number(m[1]) : zoom;
    pinch = null;
    setZoom(z);
  });

  async function load(): Promise<void> {
    const ld = loader(t('prohlizec.pdf_loading'));
    el.appendChild(ld.el);
    try {
      const lib = await loadLib(opts.assetsBase);
      loading = lib.getDocument({
        url: opts.url, cMapUrl: opts.assetsBase + 'cmaps/', cMapPacked: true, standardFontDataUrl: opts.assetsBase + 'standard_fonts/',
        wasmUrl: opts.assetsBase + 'wasm/', iccUrl: opts.assetsBase + 'iccs/', isEvalSupported: false, enableScripting: false,
      });
      loading.onProgress = (p) => ld.progress(p.loaded, p.total || null);
      doc = await loading.promise;
      if (destroyed) { void loading.destroy(); return; }
      const first = await doc.getPage(1);
      const v = first.getViewport({ scale: 1 });
      fitScale = Math.max(0.2, (el.clientWidth - 32) / v.width);
      for (let i = 1; i <= doc.numPages; i++) {
        const wrap = h('div', { class: 'viewer-pdf-page', dataset: { page: String(i) } }, h('span', { class: 'viewer-pdf-num' }, String(i)));
        const s: PageSlot = { wrap, canvas: null, page: i === 1 ? first : null, w: v.width, h: v.height, renderedScale: 0, task: null, text: null };
        size(s);
        slots.push(s);
        pagesEl.appendChild(wrap);
        observer.observe(wrap);
      }
      status();
      ld.remove();
    } catch (e) {
      ld.remove();
      if (!destroyed) opts.onError((e as Error).message || String(e));
    }
  }
  void load();

  async function textOf(i: number): Promise<string> {
    const s = slots[i];
    if (!s || !doc) return '';
    if (s.text === null) {
      s.page ??= await doc.getPage(i + 1);
      const tc = await s.page.getTextContent();
      s.text = tc.items.map((x) => x.str ?? '').join(' ').toLowerCase();
    }
    return s.text;
  }

  return {
    el,
    zoomIn: () => setZoom(zoom * 1.25),
    zoomOut: () => setZoom(zoom / 1.25),
    fit: () => { const s = slots[0]; if (s) fitScale = Math.max(0.2, (el.clientWidth - 32) / s.w); zoom = 1.0001; setZoom(1, false); },
    goTo: (n) => { const s = slots[Math.max(0, Math.min(slots.length - 1, n - 1))]; if (s) { el.scrollTop = s.wrap.offsetTop - 12; current = n; status(); } },
    find: async (q) => {
      const needle = q.trim().toLowerCase();
      hits = [];
      hitIndex = -1;
      if (!needle) { extra = undefined; status(); return; }
      for (let i = 0; i < slots.length; i++) {
        if (destroyed) return;
        const txt = await textOf(i);
        const count = txt.split(needle).length - 1;
        if (count > 0) hits.push({ page: i + 1, count });
      }
      const total = hits.reduce((n, x) => n + x.count, 0);
      extra = total ? t('prohlizec.pdf_found', { n: total, pages: hits.length }) : t('prohlizec.pdf_not_found', { q });
      if (hits.length) { hitIndex = 0; const first = hits[0]; if (first) { const s = slots[first.page - 1]; if (s) { el.scrollTop = s.wrap.offsetTop - 12; current = first.page; } } }
      status();
    },
    findNext: (dir) => {
      if (!hits.length) return;
      hitIndex = (hitIndex + dir + hits.length) % hits.length;
      const hit = hits[hitIndex];
      if (hit) { const s = slots[hit.page - 1]; if (s) { el.scrollTop = s.wrap.offsetTop - 12; current = hit.page; status(); } }
    },
    hasHits: () => hits.length > 0,
    destroy: () => {
      destroyed = true;
      observer.disconnect();
      for (const s of slots) release(s);
      void loading?.destroy();
      loading = null;
      doc = null;
    },
  };
}
